import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { Row, Col, Button } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import Table from '@components/table';
import { FORM_MODE, Permissions } from '@awp/constants';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import { getAvalancheGeographicAreas } from '@awp/stores/avalancheSlice';
import Authorize from '@awp/components/Authorize';
import { FormSelect } from '@awp/components/Common/AwpFormInputs';
import { ActionMenuView, ActionMenuEdit } from '@awp/components/Common/AwpActionMenus';
import RoadClosureLocationsService from '@components/RoadClosureLocationsAdmin/RoadClosureLocationsService';

const RoadClosureLocationsTable = ({ isMedium, isLarge, setMode, mode, area, setArea, setData, areaPoints }) => {
  const programAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));

  const [areaList, setAreaList] = useState(avalancheAreas.filter((x) => !x.isDisabled));
  const [pointList, setPointList] = useState(areaPoints.filter((x) => !x.isDisabled));

  const avalanchePrograms = programAreas.filter((x) => !x.isDisabled);

  const crudService = new RoadClosureLocationsService(mode);
  const { readOnly } = crudService.crud;

  useEffect(() => {
    setPointList(
      areaPoints.filter((x) => x.areaId === area.value && !x.isDisabled).sort((a, b) => b.name.localeCompare(a.name)),
    );
  }, []);

  const programOnChange = (opt, setFieldValue) => {
    setFieldValue('area', {}, false);
    area.value = null;
    const filterCriteria =
      mode !== FORM_MODE.EDITE ? (x) => x.parentId === opt.value && !x.isDisabled : (x) => x.parentId === opt.value;
    setAreaList(avalancheAreas.filter(filterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
    setPointList([]);
  };

  const areaOnChange = (opt, setFieldValue) => {
    setPointList(areaPoints.filter((x) => x.areaId === opt.value).sort((a, b) => b.name.localeCompare(a.name)));
    setArea(opt);
    var program = avalanchePrograms.find((x) => x.value === opt.parentId);
    setFieldValue('program', program);
    const filterCriteria =
      mode !== FORM_MODE.EDIT
        ? (x) => x.parentId === program.value && !x.isDisabled
        : (x) => x.parentId === program.value;
    setAreaList(avalancheAreas.filter(filterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
  };

  const actionMenu = (row) => {
    return (
      <>
        <div>
          <Authorize requires={[Permissions.WriteAdminTools]}>
            <ActionMenuEdit
              onClick={() => {
                setData(row.row.original);
                setMode(FORM_MODE.EDIT);
              }}
            />
          </Authorize>
          <ActionMenuView
            onClick={() => {
              setData(row.row.original);
              setMode(FORM_MODE.VIEW);
            }}
          />
        </div>
      </>
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        style: { width: '100rem' },
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Description',
        accessor: 'description',
        style: { width: '100rem' },
        sortType: sortByNumericThenAlpha,
        Cell: (row) => {
          const data = row.row.original;
          const id = `row-description-${data.value}`;
          return (
            <>
              <span data-tooltip-content data-tooltip-id={id}>
                {row.value?.length > 50 ? row.value?.slice(0, 47) + '...' : row.value}
              </span>
            </>
          );
        },
      },
      {
        Header: 'Active',
        accessor: 'isDisabled',
        style: { width: '30rem' },
        sortType: sortByNumericThenAlpha,
        Cell: (c) => {
          if (c && c.row && c.row.original) {
            const r = !c.row.original.isDisabled ? 'Active' : 'Inactive';
            return <div className="text-wrap">{r}</div>;
          }
          return '';
        },
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        style: { width: '15rem' },
        disableSortBy: true,
        Cell: actionMenu,
      },
    ],
    [isLarge, isMedium],
  );
  return (
    <>
      <Formik
        enableReinitialize
        validateOnMount
        validateOnBlur
        validateOnChange
        initialValues={{
          validateOnMount: true,
          program: avalanchePrograms.find((x) => x.value === area.parentId),
          area: area ?? null,
        }}
        validationSchema={Yup.object({
          program: Yup.object().nullable(),
          area: Yup.object().nullable(),
        })}
      >
        {(props) => (
          <>
            <div className="d-flex">{crudService.crud.getHeader()}</div>
            <Form>
              <Row>
                <Col sm={6}>
                  <FormSelect
                    className="avalanche-select"
                    readOnly={readOnly}
                    name="program"
                    id="program"
                    label="Program"
                    useMargin={true}
                    postChange={(e) => {
                      programOnChange(e, props.setFieldValue);
                    }}
                    options={avalanchePrograms}
                    isMulti={false}
                  />
                </Col>
                <Col sm={6}>
                  <FormSelect
                    className="avalanche-select"
                    readOnly={readOnly}
                    name="area"
                    id="area"
                    label="Avalanche Area"
                    useMargin={true}
                    postChange={(e) => {
                      areaOnChange(e, props.setFieldValue);
                    }}
                    options={areaList}
                    isMulti={false}
                  />
                </Col>
              </Row>
            </Form>
          </>
        )}
      </Formik>
      {area?.value ? (
        <>
          <Table columns={columns} data={pointList} fetchData={() => {}} search="" defaultSortBy="name" />
        </>
      ) : (
        <div className="mt-4">
          <p>Please select a program and an area to display the table.</p>
        </div>
      )}
      <div style={{ paddingTop: '1.25rem' }}>
        <Authorize requires={[Permissions.WriteAdminTools]}>
          <Button
            className="mr-2"
            variant="primary"
            onClick={() => {
              setMode('create');
            }}
          >
            Create
          </Button>
        </Authorize>
      </div>
    </>
  );
};

export default RoadClosureLocationsTable;
